import CustomTextCard from "../../../common/components/card/CustomTextCard";
import styled from "styled-components";
import { C1Text, T3Text } from "../../../common/components/text/CustomText";
import { Divider, Row, SingleScrollView, SizedBox } from "../../../common/components/CommonBaseComponents";
import CustomDateRangeButton from "../../../common/components/button/CustomDateRangeButton";
import { useEffect, useState } from "react";
import {
    format,
    subDays,
    subWeeks,
    subMonths,
    subYears,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfYear,
    endOfYear,
} from "date-fns";
import StoreService from "../../../service/StoreService";
import CustomSegmentedControl from "../../../common/components/button/CustomSegmentedControl";
import SecondaryButton from "../../../common/components/button/SecondaryButton";
import CustomPieChart from "../../../common/components/chart/CustomPieChart";
import { CustomLegend } from "../../../common/components/chart/CustomGrpahCommonComponents";
import RevenueDetailCard from "../components/RevenueDetailCard";
import CustomSubTab from "../../../common/components/tab/CustomSubTab";
import Translate from "../../../common/translate/Translate";
import { Theme } from "../../../common/Theme";
import ExcelUtil from "../../../common/utils/ExcelUtil";

const WebTextCardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 17px;
    height: 170px;
    width: 100%;
`;

const WebChartGrid = styled.div`
    display: grid;
    grid-template-columns: 0.9fr 1.1fr;
    gap: 17px;
    height: 493px;
    width: 100%;
`;

const dateRangeValues = [
    { label: "Day", value: "day" },
    { label: "Week", value: "week" },
    { label: "Month", value: "month" },
    { label: "Year", value: "year" },
];

// periodType 별 엑셀 첫 칼럼 헤더
const periodColumnLabel = {
    day: "Date",
    week: "Week",
    month: "Month",
    year: "Year",
};

// periodType 별 default 조회 기간 (버튼 클릭 시 초기값) → { from: Date, to: Date }
function getDateRange(period) {
    const today = new Date();
    switch (period) {
        case "week":
            // 5주 전 ~ 이번 주
            return { from: startOfWeek(subWeeks(today, 5)), to: endOfWeek(today) };
        case "month":
            // 12개월 전 ~ 이번 달
            return { from: startOfMonth(subMonths(today, 12)), to: endOfMonth(today) };
        case "year":
            // 최근 5년 (5년 전 ~ 올해)
            return { from: startOfYear(subYears(today, 5)), to: endOfYear(today) };
        case "day":
        default:
            // 최근 7일
            return { from: subDays(today, 6), to: today };
    }
}

// 응답이 배열이 아닌 형태(객체 래핑 등)로 내려와도 안전하게 배열로 정규화
function toRevenueList(result) {
    if (Array.isArray(result)) return result;
    if (result && typeof result === "object") {
        const nested = result.list ?? result.items ?? result.totalRevenue ?? result.result;
        if (Array.isArray(nested)) return nested;
    }
    return [];
}

// totalRevenue 배열을 순회하며 washer/dryer/combo 의 특정 필드(regular/courtesy) 합산
function sumByMachine(list, key) {
    return toRevenueList(list).reduce(
        (acc, item) => ({
            washer: acc.washer + (item.washer?.[key] ?? 0),
            dryer: acc.dryer + (item.dryer?.[key] ?? 0),
            combo: acc.combo + (item.combo?.[key] ?? 0),
        }),
        { washer: 0, dryer: 0, combo: 0 }
    );
}

// API 응답 항목을 RevenueDetailCard props 로 변환 (기간 구분 필드: date/hour/dayofweek)
function toRevenueDetail(item) {
    const label = item.date ?? (item.hour != null ? `${String(item.hour).padStart(2, "0")}:00` : item.dayofweek) ?? "-";
    const washerRegular = item.washer?.regular ?? 0;
    const washerCourtesy = item.washer?.courtesy ?? 0;
    const dryerRegular = item.dryer?.regular ?? 0;
    const dryerCourtesy = item.dryer?.courtesy ?? 0;
    const comboRegular = item.combo?.regular ?? 0;
    const comboCourtesy = item.combo?.courtesy ?? 0;
    return {
        label,
        totalRegularCycle: washerRegular + dryerRegular + comboRegular,
        totalCourtesyCycle: washerCourtesy + dryerCourtesy + comboCourtesy,
        washer: { regularCycle: washerRegular, courtesyCycle: washerCourtesy },
        dryer: { regularCycle: dryerRegular, courtesyCycle: dryerCourtesy },
        combo: { regularCycle: comboRegular, courtesyCycle: comboCourtesy },
    };
}

function ReportStatusSubTabContent({ deviceSubTabs, handleSetSubTab, storeId }) {
    const storeService = new StoreService();
    const [selectedDateRange, setSelectedDateRange] = useState("day");
    const [dateRange, setDateRange] = useState(() => getDateRange("day"));
    const [revenueSummary, setRevenueSummary] = useState(null);
    const [totalRevenue, setTotalRevenue] = useState([]);

    useEffect(() => {
        if (!storeId) return;
        const today = new Date().toISOString().slice(0, 10);
        storeService
            .getStoreRevenueSummaryByStore({ storeId, today })
            .then((result) => {
                setRevenueSummary(result);
            })
            .catch((error) => {
                console.error("Error fetching store revenue summary:", error);
            });
    }, [storeId]);

    // day/week/month/year 선택 시 조회 기간을 해당 periodType 의 default 로 초기화
    useEffect(() => {
        setDateRange(getDateRange(selectedDateRange));
    }, [selectedDateRange]);

    // Revenue By Period: periodType(day/week/month/year) 또는 조회 기간(startDate/endDate) 변경 시
    // 매장별 수익 조회 (GET /report/revenue/<store-id>) 호출
    useEffect(() => {
        if (!storeId) return;
        if (!dateRange?.from || !dateRange?.to) return;
        storeService
            .getStoreRevenueByStore({
                storeId,
                periodType: selectedDateRange,
                startDate: format(dateRange.from, "yyyy-MM-dd"),
                endDate: format(dateRange.to, "yyyy-MM-dd"),
            })
            .then((result) => {
                setTotalRevenue(toRevenueList(result));
            })
            .catch((error) => {
                console.error("Error fetching store revenue by store:", error);
                setTotalRevenue([]);
            });
    }, [storeId, selectedDateRange, dateRange]);

    // 현재 선택된 기간(day/week/month/year)의 Revenue Detail 을 엑셀로 다운로드
    const handleExportExcel = () => {
        const columns = [
            { key: "label", label: periodColumnLabel[selectedDateRange] ?? "Period", text: true },
            { key: "totalRegularCycle", label: "Total Regular" },
            { key: "totalCourtesyCycle", label: "Total Courtesy" },
            { key: "washerRegular", label: "Washer Regular" },
            { key: "washerCourtesy", label: "Washer Courtesy" },
            { key: "dryerRegular", label: "Dryer Regular" },
            { key: "dryerCourtesy", label: "Dryer Courtesy" },
            { key: "comboRegular", label: "Combo Regular" },
            { key: "comboCourtesy", label: "Combo Courtesy" },
        ];
        const rows = totalRevenue.map((item) => {
            const detail = toRevenueDetail(item);
            // week 는 주 시작일(yyyy-MM-dd) 을 ISO 주차(yyyy-Www) 형식으로 변환
            const label =
                selectedDateRange === "week" && item.date ? format(new Date(item.date), "RRRR-'W'II") : detail.label;
            return {
                label: label,
                totalRegularCycle: detail.totalRegularCycle,
                totalCourtesyCycle: detail.totalCourtesyCycle,
                washerRegular: detail.washer.regularCycle,
                washerCourtesy: detail.washer.courtesyCycle,
                dryerRegular: detail.dryer.regularCycle,
                dryerCourtesy: detail.dryer.courtesyCycle,
                comboRegular: detail.combo.regularCycle,
                comboCourtesy: detail.combo.courtesyCycle,
            };
        });
        ExcelUtil.download({ fileName: `revenue_detail_${selectedDateRange}`, columns, rows });
    };

    const regularSum = sumByMachine(totalRevenue, "regular");
    const courtesySum = sumByMachine(totalRevenue, "courtesy");

    const regularPieData = [
        { name: "Washer", value: regularSum.washer, color: Theme.color.cyan500 },
        { name: "Dryer", value: regularSum.dryer, color: Theme.color.orange500 },
        { name: "Combo", value: regularSum.combo, color: Theme.color.indigo800 },
    ];
    const courtesyPieData = [
        { name: "Washer", value: courtesySum.washer, color: Theme.color.cyan500 },
        { name: "Dryer", value: courtesySum.dryer, color: Theme.color.orange500 },
        { name: "Combo", value: courtesySum.combo, color: Theme.color.indigo800 },
    ];

    return (
        <>
            <CustomSubTab tabs={deviceSubTabs} activeTab={"Status"} onClick={handleSetSubTab} />
            <SizedBox height={"20px"} />
            <WebTextCardGrid>
                <CustomTextCard
                    icon={"bank_note"}
                    title={Translate("dashboard.todayRevenue")}
                    mainContent={
                        revenueSummary?.today != null ? `฿${Number(revenueSummary.today).toLocaleString()}` : "฿0.00"
                    }
                    hasIcon={true}
                />
                <CustomTextCard
                    icon={"bar_down"}
                    title={Translate("dashboard.dailyRevenueAverage")}
                    mainContent={
                        revenueSummary?.average != null
                            ? `฿${Number(revenueSummary.average).toLocaleString()}`
                            : "฿0.00"
                    }
                    hasIcon={true}
                    leftTopText={"Last Month"}
                />
                <CustomTextCard
                    icon={"trend_up"}
                    title={Translate("dashboard.highestDailyRevenue")}
                    mainContent={
                        revenueSummary?.best != null ? `฿${Number(revenueSummary.best).toLocaleString()}` : "฿0.00"
                    }
                    hasIcon={true}
                    leftTopText={"Last Month"}
                />
            </WebTextCardGrid>
            <SizedBox height={"16px"} />
            <Row justifyContent={"space-between"} alignItem={"flex-end"} padding={"0 12px"} width={"100%"}>
                <T3Text fontWeight={"600"}>{Translate("store.revenueByPeriod")}</T3Text>
                <Row>
                    <CustomSegmentedControl
                        values={dateRangeValues}
                        currentValue={selectedDateRange}
                        setValue={setSelectedDateRange}
                    />
                    <SizedBox width={"10px"} />
                    <CustomDateRangeButton range={dateRange} setRange={setDateRange} periodType={selectedDateRange} />
                    <SizedBox width={"10px"} />
                    <SecondaryButton
                        icon={"download"}
                        text={Translate("common.exportExcel")}
                        onClick={handleExportExcel}
                    />
                </Row>
            </Row>
            <SizedBox height={"12px"} />
            <WebChartGrid>
                <SizedBox
                    border={`1px solid ${Theme.color.gray250}`}
                    borderRadius={"16px"}
                    width={"100%"}
                    height={"100%"}
                    alignItem={"flex-start"}
                    justifyContent={"flex-start"}
                    padding={"16px"}
                    backgroundColor={"transparent"}
                >
                    <C1Text fontWeight={"600"}>{Translate("store.revenueChart")}</C1Text>
                    <Row width={"100%"} height={"80%"} justifyContent={"center"}>
                        <CustomPieChart title={Translate("store.revenueChart.regularCycle")} data={regularPieData} />
                        <Divider
                            orientation={"vertical"}
                            width={"1px"}
                            height={"72px"}
                            backgroundColor={Theme.color.gray200}
                        />
                        <CustomPieChart title={Translate("store.revenueChart.courtesyCycle")} data={courtesyPieData} />
                    </Row>
                    <Row width={"100%"} justifyContent={"center"}>
                        <CustomLegend
                            paddingTop={"0px"}
                            payload={[
                                { value: "Washer", color: Theme.color.cyan500 },
                                { value: "Dryer", color: Theme.color.orange500 },
                                { value: "Combo", color: Theme.color.indigo800 },
                            ]}
                        />
                    </Row>
                </SizedBox>
                <SizedBox
                    border={`1px solid ${Theme.color.gray250}`}
                    borderRadius={"16px"}
                    width={"100%"}
                    height={"100%"}
                    alignItem={"flex-start"}
                    justifyContent={"flex-start"}
                    padding={"16px"}
                    backgroundColor={"transparent"}
                >
                    <C1Text fontWeight={"600"}>{Translate("store.revenueDetail")}</C1Text>
                    <SizedBox height={"40px"} />
                    <SingleScrollView $flex={"1"} $overflowY={"auto"} $gridTemplateColumns={"repeat(3, 1fr)"} $gap={32}>
                        {totalRevenue.map((item, index) => (
                            <RevenueDetailCard
                                key={`${item.date ?? item.hour ?? item.dayofweek}-${index}`}
                                {...toRevenueDetail(item)}
                            />
                        ))}
                    </SingleScrollView>
                </SizedBox>
            </WebChartGrid>
        </>
    );
}

export default ReportStatusSubTabContent;
